<template>
  <div>
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <h2 class="mt-6 text-center text-2xl font-medium text-brand-gray-900">
        Enter Shipping Information
      </h2>
      <p class="mt-2 text-center text-sm text-brand-gray-600">
        Your registration includes a workbook that will be shipped to you. Please enter your shipping information below.
      </p>
    </div>
    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-screen-sm">
      <div class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
        <form class="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8" action="#" method="POST"
              @submit.prevent="submit">
          <div class="col-span-2 grid grid-cols-1 gap-y-6 md:grid-cols-3 sm:gap-x-8">
            <h3 class="col-span-3 mt-2 text-center text-lg font-medium text-brand-gray-900">Shipping Information</h3>
            <div class="col-span-3">
              <div class="relative border border-brand-gray-300 rounded-md px-3 py-2 shadow-sm focus-within:ring-1 focus-within:ring-purple-600 focus-within:border-purple-600">
                <label for="country"
                       class="absolute -top-2 left-2 -mt-px inline-block px-1 bg-white text-xs font-medium text-brand-gray-900">Country</label>
                <v-select :options="countries"
                          v-model="form.shippingAddress.country"
                          :reduce="country => country.code"
                          @option:selected="countrySelected"
                          class="w-full"></v-select>
              </div>
              <p v-if="hasError('shippingAddress.country')" class="mt-2 text-sm text-red-600">{{ errors['shippingAddress.country'][0] }}</p>
            </div>
            <div class="col-span-3 grid grid-cols-1 gap-y-6 md:grid-cols-3 sm:gap-x-8" v-show="showAddress">
              <div class="col-span-3">
                <div class="relative border border-brand-gray-300 rounded-md px-3 py-2 shadow-sm focus-within:ring-1 focus-within:ring-purple-600 focus-within:border-purple-600">
                  <label for="shipping"
                         class="absolute -top-2 left-2 -mt-px inline-block px-1 bg-white text-xs font-medium text-brand-gray-900">Street Address (No PO Boxes)</label>
                  <input type="text" v-model="shippingAddress" id="shipping"
                         class="block w-full border-0 p-0 text-brand-gray-900 placeholder-brand-gray-500 focus:ring-0 sm:text-sm"
                         placeholder="123 A Street, Brookhaven, MS 39601">
                </div>
                <p v-if="hasError('shippingAddress.address1')" class="mt-2 text-sm text-red-600">{{ errors['shippingAddress.address1'][0] }}</p>
              </div>
              <div class="col-span-3">
                <div
                    class="relative border border-brand-gray-300 rounded-md px-3 py-2 shadow-sm focus-within:ring-1 focus-within:ring-purple-600 focus-within:border-purple-600">
                  <label for="address2"
                         class="absolute -top-2 left-2 -mt-px inline-block px-1 bg-white text-xs font-medium text-brand-gray-900">
                    Apartment, suite, etc. (optional)
                  </label>
                  <input type="text" v-model="form.shippingAddress.address2" id="address2" autocomplete="shipping address-line2"
                         class="block w-full border-0 p-0 text-brand-gray-900 placeholder-brand-gray-500 focus:ring-0 sm:text-sm"
                         placeholder="Apartment 4A">
                </div>
                <p v-if="hasError('shippingAddress.address2')" class="mt-2 text-sm text-red-600">{{ errors['shippingAddress.address2'][0] }}</p>
              </div>
              <div>
                <div
                    class="relative border border-brand-gray-300 rounded-md px-3 py-2 shadow-sm focus-within:ring-1 focus-within:ring-purple-600 focus-within:border-purple-600">
                  <label for="city"
                         class="absolute -top-2 left-2 -mt-px inline-block px-1 bg-white text-xs font-medium text-brand-gray-900">
                    City
                  </label>
                  <input type="text" v-model="form.shippingAddress.city" id="city"
                         class="block w-full border-0 p-0 text-brand-gray-900 placeholder-brand-gray-500 focus:ring-0 sm:text-sm"
                         placeholder="Brookhaven">
                </div>
                <p v-if="hasError('shippingAddress.city')" class="mt-2 text-sm text-red-600">{{ errors['shippingAddress.city'][0] }}</p>
              </div>
              <div>
                <div
                    class="relative border border-brand-gray-300 rounded-md px-3 py-2 shadow-sm focus-within:ring-1 focus-within:ring-purple-600 focus-within:border-purple-600">
                  <label for="state"
                         class="absolute -top-2 left-2 -mt-px inline-block px-1 bg-white text-xs font-medium text-brand-gray-900">
                    State/Province
                  </label>
                  <input type="text" v-model="form.shippingAddress.state" id="state"
                         class="block w-full border-0 p-0 text-brand-gray-900 placeholder-brand-gray-500 focus:ring-0 sm:text-sm"
                         placeholder="MS">
                </div>
                <p v-if="hasError('shippingAddress.state')" class="mt-2 text-sm text-red-600">{{ errors['shippingAddress.state'][0] }}</p>
              </div>
              <div>
                <div
                    class="relative border border-brand-gray-300 rounded-md px-3 py-2 shadow-sm focus-within:ring-1 focus-within:ring-purple-600 focus-within:border-purple-600">
                  <label for="postal_code"
                         class="absolute -top-2 left-2 -mt-px inline-block px-1 bg-white text-xs font-medium text-brand-gray-900">
                    Postal Code
                  </label>
                  <input type="text" v-model="form.shippingAddress.postal_code" id="postal_code"
                         class="block w-full border-0 p-0 text-brand-gray-900 placeholder-brand-gray-500 focus:ring-0 sm:text-sm"
                         placeholder="39601">
                </div>
                <p v-if="hasError('shippingAddress.postal_code')" class="mt-2 text-sm text-red-600">{{ errors['shippingAddress.postal_code'][0] }}</p>
              </div>
            </div>
          </div>
          <div class="sm:col-span-2 flex justify-center" v-show="showAddress">
            <button type="submit"
                    :disabled="submitting"
                    class="w-full sm:w-1/2 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:bg-purple-300 disabled:cursor-wait">
                            <span class="flex" v-if="submitting">
                                <svg class="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none"
                                     viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                        stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg> Saving
                            </span>
              <span v-else>Continue</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css';

export default {
  name: "ShippingInformation",

  components: {
    vSelect
  },

  data() {
    return {
      autocompleteAddress: null,
      autocompleteAddressListener: null,
      errors: {},
      form: {
        shippingAddress: {
          country: null,
          address1: null,
          address2: null,
          city: null,
          state: null,
          street_number: null,
          route: null,
          locality: null,
          administrative_area_level_1: null,
          country: null,
          postal_code: null,
          postal_code_suffix: null
        }
      },
      regions: [],
      shippingAddress: null,
      showAddress: false,
      submitting: false,
      mapsScript: null
    };
  },

  mounted() {
    this.mapsScript = document.createElement('script');
    this.mapsScript.setAttribute('src',
        'https://maps.googleapis.com/maps/api/js?key=AIzaSyDVx_WowNgwEq6yOXVVjp7Xt71iqUwcT_8&libraries=places&callback=initAutocomplete');
    this.mapsScript.async = true;
    document.head.appendChild(this.mapsScript);

    this.fetchRegions();

    window.initAutocomplete = () => {
      this.autocompleteAddress = new google.maps.places.Autocomplete(document.getElementById('shipping'),
          {types: ['geocode']});
      this.autocompleteAddressListener = this.autocompleteAddress.addListener('place_changed', this.addressSelected);
    };


  },

  watch: {
    shippingAddress(newAddress){
      this.form.shippingAddress.address1 = newAddress;
    }
  },

  computed: {
    countries(){
      return this.regions.map(region => ({'code': region.iso, 'label': region.country}));
    }
  },

  methods: {
    async addressSelected() {
      let place = this.autocompleteAddress.getPlace();

      this.latitude = place.geometry.location.lat();
      this.longitude = place.geometry.location.lng();

      place.address_components.forEach((item) => {
        if(item.types[0] != 'country'){
          this.form.shippingAddress[item.types[0]] = {long_name: item.long_name, short_name: item.short_name};
        }
      });

      if(this.form.shippingAddress.postal_code && this.form.shippingAddress.postal_code_suffix){
        this.form.shippingAddress.postal_code = `${this.form.shippingAddress.postal_code.short_name}-${this.form.shippingAddress.postal_code_suffix.short_name}`;
      } else if(this.form.shippingAddress.postal_code){
        this.form.shippingAddress.postal_code = this.form.shippingAddress.postal_code.short_name;
      }

      this.form.shippingAddress.city = this.form.shippingAddress.locality.short_name;
      this.form.shippingAddress.state = this.form.shippingAddress.administrative_area_level_1.short_name;

      if(this.form.shippingAddress.street_number && this.form.shippingAddress.route && this.form.shippingAddress.city && this.form.shippingAddress.state){
        this.form.shippingAddress.address1 = `${this.form.shippingAddress.street_number.short_name} ${this.form.shippingAddress.route.short_name}`;
      } else{
        // console.log("Disable autocomplete and require manual entry.");
        this.$nextTick(() => {
          this.autocompleteAddressListener.remove();
          document.querySelectorAll('.pac-container').forEach(el => el.remove());
        });
        this.form.shippingAddress.address1 = this.shippingAddress;

        if(!this.errors['shippingAddress.address1']){
          this.errors['shippingAddress.address1'] = [];
          this.errors['shippingAddress.address1'].push("Address not matched. Please manually enter it.")
        } else{
          this.errors['shippingAddress.address1'][0] = ("Address not matched. Please manually enter it.");
        }
      }

      this.shippingAddress = this.form.shippingAddress.address1;
    },
    countrySelected() {
      var country = this.regions.find(region => region.iso == this.form.shippingAddress.country);

      this.autocompleteAddress.setComponentRestrictions({
        country: country.iso
      });

      this.showAddress = true;
    },

    hasError(field) {
      return this.errors.hasOwnProperty(field);
    },

    async fetchRegions(){
      try {
        const {data} = await axios.get('/api/geography/regions');
        this.regions = data;
      } catch ({response}) {
        console.log("error: ", response);
      }
    },

    toMap(list, keyProp){
      const keyValuePairs = list.map(item => [item[keyProp], item]);
      return new Map(keyValuePairs);
    },

    async submit() {
      this.submitting = true;
      this.errors = {};
      try {
        const {data} = await axios.post('/api/bookstore/update-shipping', this.form);
        this.$emit('shipping-updated', data);
      } catch ({response}) {
        this.errors = response.data;
      }
      this.submitting = false;
    },
  },
}
</script>

<style scoped>

</style>